import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import i18n from '@shared/i18n/config'
import { toastEmitter } from '@shared/lib/toastEmitter'
import { getApiErrorMessage, getApiErrorStatus, type ApiError } from './client'

// ─── Meta type augmentation ───────────────────────────────────────────────────

export const MutationMethod = {
  post: 'post',
  put: 'put',
  patch: 'patch',
  delete: 'delete',
} as const

export type MutationMethod = (typeof MutationMethod)[keyof typeof MutationMethod]

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      method?: MutationMethod
      /** Suppress all toasts for this mutation */
      silent?: boolean
      /** Suppress the error toast only for errors the caller explains itself */
      silentWhen?: (error: unknown) => boolean
    }
    queryMeta: {
      /** Suppress error toast for this query */
      silent?: boolean
    }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ToastPayload {
  title: string
  description?: string
}

function extractApiError(error: unknown): ToastPayload {
  // 401s are handled by the client (refresh + redirect) — stay silent here.
  if ((error as ApiError)?.response?.status === 401) return { title: '' }
  return { title: i18n.t('toast.error'), description: getApiErrorMessage(error) }
}

function successTitleForMethod(method: MutationMethod | undefined): string {
  switch (method) {
    case MutationMethod.delete:
      return i18n.t('toast.deleted')
    case MutationMethod.patch:
    case MutationMethod.put:
      return i18n.t('toast.updated')
    case MutationMethod.post:
      return i18n.t('toast.created')
    default:
      return i18n.t('toast.success')
  }
}

// ─── QueryClient ──────────────────────────────────────────────────────────────

/** Повторяем один раз только сбой сети и 5xx. 4xx — ответ бэка по существу
 *  (404 «нет текущей олимпиады», 403, 400): повтор вернёт то же самое и лишь
 *  удвоит запрос. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false
  const status = getApiErrorStatus(error)
  return status === undefined || status >= 500
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.options.meta?.silent) return
      const { title, description } = extractApiError(error)
      if (title) toastEmitter.error(title, description)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.silent) return
      if (mutation.options.meta?.silentWhen?.(error)) return
      const { title, description } = extractApiError(error)
      if (title) toastEmitter.error(title, description)
    },
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.options.meta?.silent) return
      const title = successTitleForMethod(mutation.options.meta?.method)
      toastEmitter.success(title)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})
